import React, { useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./Map.css";
import polyline from "@mapbox/polyline";
import "leaflet-polylinedecorator";
import Papa, { parse } from "papaparse";
import { saveAs } from "file-saver";
import JSZip from "jszip";

// Fix for default marker icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [51.2562, 7.1508]; // Wuppertal, Germany

const VEHICLE_PRESETS = [
  { label: 'Small Van',    icon: '🚐', capacity: 100,  skills: '1',  startHour: 8, endHour: 17, desc: 'Urban last-mile · 100 units' },
  { label: 'Medium Truck', icon: '🚛', capacity: 500,  skills: '1',  startHour: 7, endHour: 18, desc: 'Regional delivery · 500 units' },
  { label: 'Heavy HGV',   icon: '🚚', capacity: 2000, skills: '2',  startHour: 6, endHour: 18, desc: 'Long-haul freight · 2000 units' },
  { label: 'Refrigerated', icon: '❄️', capacity: 80,   skills: '5', startHour: 5, endHour: 14, desc: 'Cold chain · skill 5 · 80 units' },
  { label: 'Bicycle',      icon: '🚲', capacity: 15,   skills: '1',  startHour: 9, endHour: 18, desc: 'Micro-delivery · 15 units' },
  { label: 'Hazmat',       icon: '☢️', capacity: 300,  skills: '7', startHour: 6, endHour: 14, desc: 'Dangerous goods · skill 7 · 300 units' },
];

// Returns a datetime-local string for today at the given hour
const todayAt = (hour) => {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

const CITIES = [
  { name: 'Wuppertal',  lat: 51.2562, lng:  7.1508 },
  { name: 'Bonn',       lat: 50.7374, lng:  7.0982 },
  { name: 'Vienna',     lat: 48.2082, lng: 16.3738 },
  { name: 'Modena',     lat: 44.6471, lng: 10.9252 },
  { name: 'Klagenfurt', lat: 46.6228, lng: 14.3051 },
  { name: 'Gothenburg', lat: 57.7089, lng: 11.9746 },
  { name: 'Rome',       lat: 41.9028, lng: 12.4964 },
  { name: 'Lyon',       lat: 45.7640, lng:  4.8357 },
];

// Flies the map to a lat/lng whenever `target` changes
function MapFlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 13);
  }, [target, map]);
  return null;
}

let globalIdCounter = 1;

export default function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`);
      const data = await res.json();
      if (data.length > 0) { setSearchResults(data); setShowSearchDropdown(true); }
    } catch (e) { console.error(e); }
  };

  const selectSearchResult = (r) => {
    setFlyTarget({ lat: parseFloat(r.lat), lng: parseFloat(r.lon) });
    setShowSearchDropdown(false);
    setSearchQuery(r.display_name.split(',')[0]);
  };

  const [addMode, setAddMode] = useState(null); // "vehicle" | "job" | "shipment"
  const [vCapacity, setVCapacity] = useState('');
  const [vSkills, setVSkills]     = useState('');
  const [vStartTime, setVStartTime] = useState('');
  const [vEndTime, setVEndTime]     = useState('');

  const applyVehiclePreset = (preset) => {
    setVCapacity(String(preset.capacity));
    setVSkills(preset.skills);
    setVStartTime(todayAt(preset.startHour));
    setVEndTime(todayAt(preset.endHour));
  };

  const resetVehicleForm = () => {
    setVCapacity(''); setVSkills(''); setVStartTime(''); setVEndTime('');
  };
  const [formLocation, setFormLocation] = useState(null);
  const [editingEntity, setEditingEntity] = useState(null); // { type, data }
  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [shipmentPoints, setShipmentPoints] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [apiResponse, setApiResponse] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animStep, setAnimStep] = useState(null); // null = show all routes
  const [animRouteIdx, setAnimRouteIdx] = useState(0);

  // ---------- Helpers ----------
  const parseArray = (val) => {
    // accepts JSON array string "[1,2]" or "1,2" or single number "3"
    if (val === undefined || val === null || val === "") return [];
    const s = String(val).trim();
    try {
      if (s.startsWith("[")) return JSON.parse(s);
      if (s.includes(",")) return s.split(",").map((x) => Number(x.trim()));
      return [Number(s)];
    } catch (err) {
      // fallback: try splitting by comma
      if (s.includes(",")) return s.split(",").map((x) => Number(x.trim()));
      const num = Number(s);
      return Number.isNaN(num) ? [] : [num];
    }
  };

  const parseLocation = (row) => {
    // row can be:
    // - string "[lon, lat]" or "[lat, lon]" (we will accept both and try to detect)
    // - "lat, lon" or "lon,lat"
    // - separate columns: row.lat & row.lng or row.latitude & row.longitude
    if (!row) return null;

    // If row is object (passed entire CSV row), try known keys
    if (typeof row === "object" && !Array.isArray(row)) {
      const keys = Object.keys(row).map((k) => k.toLowerCase());
      const get = (k) => {
        const found = Object.keys(row).find((kk) => kk.toLowerCase() === k);
        return found ? row[found] : undefined;
      };
      const latVal = get("lat") ?? get("latitude");
      const lngVal = get("lng") ?? get("lon") ?? get("longitude") ?? get("long");
      if (latVal !== undefined && lngVal !== undefined) {
        const lat = Number(latVal);
        const lng = Number(lngVal);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) return [lng, lat];
      }
      // If there's a combined 'location' or 'start' field, parse it below using val
      const locVal = get("location") ?? get("start") ?? get("end") ?? get("pickup_location") ?? get("delivery_location");
      if (locVal !== undefined) {
        return parseLocation(String(locVal));
      }
      return null;
    }

    const val = String(row).trim();
    if (!val) return null;

    try {
      if (val.startsWith("[")) {
        // assume JSON array: [lon, lat] or [lat, lon]
        const arr = JSON.parse(val);
        if (Array.isArray(arr) && arr.length >= 2) {
          // we expect geo as [lon, lat] in your schema; but many people use [lat, lon].
          // Heuristic: if first element is > last element in abs? We'll assume input likely [lon, lat]
          // We'll detect by plausible lat range (-90..90) and lon (-180..180)
          const a = Number(arr[0]), b = Number(arr[1]);
          if (Math.abs(a) <= 90 && Math.abs(b) <= 90) {
            // both look like lat/lon; prefer detect: lat is -90..90, lon -180..180.
            // If a is lat and b is lon (a within lat range but b maybe >90?), swap accordingly.
            // Best approach: if a between -90..90 and b between -180..180 -> assume [lon,lat]? can't be sure.
            // We'll attempt: if first looks like lon (> 90 or > second) treat as lon.
          }
          // return as [lon, lat] if arr[0] looks like lon (abs>90) or arr[1] looks like lat (abs<=90)
          const first = Number(arr[0]), second = Number(arr[1]);
          if (Math.abs(first) <= 180 && Math.abs(second) <= 90 && Math.abs(first) > 90) {
            // first likely lon, second lat
            return [first, second];
          }
          if (Math.abs(second) <= 180 && Math.abs(first) <= 90 && Math.abs(second) > 90) {
            // second likely lon -> assume input [lat, lon]
            return [second, first];
          }
          // fallback: assume [lon, lat]
          return [first, second];
        }
      }
      // "lat, lon" or "lon, lat"
      if (val.includes(",")) {
        const parts = val.split(",").map((p) => Number(p.trim()));
        if (parts.length >= 2) {
          const a = parts[0], b = parts[1];
          // same heuristic: if a within -90..90 and b within -180..180 but a seems lat => [lon, lat] should be [b, a]
          if (Math.abs(a) <= 90 && Math.abs(b) <= 180 && Math.abs(b) > 90) {
            return [b, a];
          }
          // otherwise assume [lon, lat]
          return [a, b];
        }
      }
    } catch (err) {
      return null;
    }
    return null;
  };

  const bumpIdCounterToCover = (items) => {
    // items: array of objects with possible id field
    const ids = items
      .map((i) => (i && i.id ? Number(i.id) : NaN))
      .filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    const max = Math.max(...ids);
    if (max >= globalIdCounter) globalIdCounter = max + 1;
  };

  // ---------- Map click handling ----------
  const handleMapClick = (latlng) => {
    if (addMode === "shipment") {
      setShipmentPoints((prev) => {
        const updated = [...prev, latlng];
        if (updated.length === 2) {
          setFormLocation(updated); // trigger form
        }
        return updated;
      });
    } else {
      setFormLocation(latlng);
    }
  };

  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        if (addMode) handleMapClick(e.latlng);
      },
    });
    return null;
  };

  // ---------- Form submit handlers (vehicles/jobs/shipments) ----------
  const handleSubmitVehicle = (e) => {
    e.preventDefault();
    if (!formLocation) {
      alert("Select a location on the map first.");
      return;
    }
    const form = e.target;
    const capacity = Number(form.capacity.value);
    const skills = parseArray(form.skills.value);
    const timeStart = form.start_time.value;
    const timeEnd = form.end_time.value;

    const time_windows =
      timeStart && timeEnd
        ? [[Math.floor(new Date(timeStart).getTime() / 1000), Math.floor(new Date(timeEnd).getTime() / 1000)]]
        : undefined;

    const newVehicle = {
      id: getNextId(),
      start: [formLocation.lng, formLocation.lat],
      end: [formLocation.lng, formLocation.lat],
      capacity: [capacity],
      skills,
      time_windows,
    };

    setVehicles((prev) => {
      const next = [...prev, newVehicle];
      bumpIdCounterToCover(next);
      return next;
    });
    setFormLocation(null);
    setAddMode(null);
    resetVehicleForm();
    e.target.reset();
  };

  const handleSubmitJob = (e) => {
    e.preventDefault();
    if (!formLocation) {
      alert("Select a location on the map first.");
      return;
    }
    const form = e.target;
    const service = Number(form.service.value);
    const jobType = form.jobType.value; // pickup or delivery
    const quantity = Number(form.quantity.value);
    const skills = parseArray(form.skills.value);

    const newJob = {
      id: getNextId(),
      service,
      location: [formLocation.lng, formLocation.lat],
      skills,
    };

    if (jobType === "pickup") newJob.pickup = [quantity];
    else newJob.delivery = [quantity];

    // check compatibility
    const compatibleVehicle = vehicles.some((vehicle) =>
      skills.every((skill) => vehicle.skills && vehicle.skills.includes(skill))
    );
    if (!compatibleVehicle) {
      const proceed = window.confirm("No vehicle has all the required skills for this job."+
        "\n\nAdd the job anyway?");
        if (proceed) {
          setJobs((prev) => {
            const next = [...prev, newJob];
            bumpIdCounterToCover(next);
            return next;
          });
        } else {
          return;
        }
    } else {
      setJobs((prev) => {
      const next = [...prev, newJob];
      bumpIdCounterToCover(next);
      return next;
    });
    }

    
    setFormLocation(null);
    setAddMode(null);
    e.target.reset();
  };

  const handleSubmitShipment = (e) => {
    e.preventDefault();
    if (!formLocation || !Array.isArray(formLocation) || formLocation.length < 2) {
      alert("Select pickup and delivery points on the map first (two clicks).");
      return;
    }
    const form = e.target;
    const amount = Number(form.amount.value);
    const skills = parseArray(form.skills.value);
    const servicePickup = Number(form.servicePickup.value);
    const serviceDelivery = Number(form.serviceDelivery.value);

    const pickupLatlng = formLocation[0];
    const deliveryLatlng = formLocation[1];
    const pickupId = getNextId();
    const deliveryId = getNextId();

    const newShipment = {
      amount: [amount],
      skills,
      pickup: {
        id: pickupId,
        service: servicePickup,
        location: [pickupLatlng.lng, pickupLatlng.lat],
      },
      delivery: {
        id: deliveryId,
        service: serviceDelivery,
        location: [deliveryLatlng.lng, deliveryLatlng.lat],
      },
    };

    // compatibility
    const compatibleVehicle = vehicles.some((vehicle) =>
      skills.every((skill) => vehicle.skills && vehicle.skills.includes(skill))
    );
    if (!compatibleVehicle) {
      const proceed = window.confirm("No vehicle has all the required skills for this job."+
        "\n\nAdd the job anyway?");
        if (proceed) {
          setShipments((prev) => {
            const next = [...prev, newShipment];
            bumpIdCounterToCover(next);
            return next;
          });
        } else {
          return;
        }
    } else {
      setShipments((prev) => {
      const next = [...prev, newShipment];
      bumpIdCounterToCover(next);
      return next;
    });
    } 

    setShipmentPoints([]);
    setFormLocation(null);
    setAddMode(null);
    e.target.reset();
  };

  // ---------- Edit handlers ----------
  const handleUpdateVehicle = (e, id) => {
    e.preventDefault();
    const form = e.target;
    const capacity = Number(form.capacity.value);
    const skills = parseArray(form.skills.value);
    const timeStart = form.start_time.value;
    const timeEnd = form.end_time.value;
    const time_windows = timeStart && timeEnd
      ? [[Math.floor(new Date(timeStart).getTime() / 1000), Math.floor(new Date(timeEnd).getTime() / 1000)]]
      : undefined;
    setVehicles((prev) => prev.map((v) =>
      v.id === id ? { ...v, capacity: [capacity], skills, time_windows } : v
    ));
    setEditingEntity(null);
  };

  const handleUpdateJob = (e, id) => {
    e.preventDefault();
    const form = e.target;
    const service = Number(form.service.value);
    const jobType = form.jobType.value;
    const quantity = Number(form.quantity.value);
    const skills = parseArray(form.skills.value);
    setJobs((prev) => prev.map((j) => {
      if (j.id !== id) return j;
      const updated = { ...j, service, skills };
      delete updated.pickup;
      delete updated.delivery;
      if (jobType === 'pickup') updated.pickup = [quantity];
      else updated.delivery = [quantity];
      return updated;
    }));
    setEditingEntity(null);
  };

  const handleUpdateShipment = (e, pickupId) => {
    e.preventDefault();
    const form = e.target;
    const amount = Number(form.amount.value);
    const skills = parseArray(form.skills.value);
    const servicePickup = Number(form.servicePickup.value);
    const serviceDelivery = Number(form.serviceDelivery.value);
    setShipments((prev) => prev.map((s) =>
      s.pickup?.id === pickupId
        ? { ...s, amount: [amount], skills, pickup: { ...s.pickup, service: servicePickup }, delivery: { ...s.delivery, service: serviceDelivery } }
        : s
    ));
    setEditingEntity(null);
  };

  // ---------- Delete handlers ----------
  const handleDeleteVehicle = (id) => {
    const vehicle = vehicles.find((v) => v.id === id);
    if (!vehicle) return;

    const usedByJob = jobs.some((job) => job.skills && job.skills.every((skill) => vehicle.skills.includes(skill)));
    const usedByShipment = shipments.some((ship) => ship.skills && ship.skills.every((skill) => vehicle.skills.includes(skill)));

    if (usedByJob || usedByShipment) {
      if (!window.confirm("This vehicle may be required by existing jobs/shipments. Delete anyway?")) return;
    }

    setVehicles((prev) => prev.filter((v) => v.id !== id));
  };

  const handleDeleteJob = (id) => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  };

  const handleDeleteShipment = (id) => {
    setShipments((prev) => prev.filter((s) => s.id !== id));
  };


  // ---------- CSV upload ----------
  const CSV_LIMITS = {
    FILE_SIZE_MB: 2,
    VEHICLES:  { warn: 150, max: 200 },
    JOBS:      { warn: 800, max: 1000 },
    SHIPMENTS: { warn: 400, max: 500 },  // each shipment = 2 Vroom locations
  };

  const isValidCoord = (arr) =>
    Array.isArray(arr) && arr.length >= 2 &&
    arr[1] >= -90 && arr[1] <= 90 &&
    arr[0] >= -180 && arr[0] <= 180;

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ── File type check ──
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Only .csv files are supported.');
      e.target.value = '';
      return;
    }

    // ── File size check ──
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > CSV_LIMITS.FILE_SIZE_MB) {
      alert(`File is ${sizeMB.toFixed(1)} MB — maximum allowed is ${CSV_LIMITS.FILE_SIZE_MB} MB.\nSplit the file into smaller batches.`);
      e.target.value = '';
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        if (data.length === 0) {
          alert('CSV is empty or has no valid rows.');
          return;
        }

        const cols = Object.keys(data[0]);
        const rowErrors = [];

        // ════════════════════════════════════
        // 🚗 VEHICLES
        // ════════════════════════════════════
        if (cols.includes('start') && cols.includes('capacity')) {
          const required = ['start', 'capacity'];
          const missing = required.filter(c => !cols.includes(c));
          if (missing.length) { alert(`Missing required columns: ${missing.join(', ')}`); return; }

          const { warn, max } = CSV_LIMITS.VEHICLES;
          const total = vehicles.length + data.length;

          if (data.length > max) {
            alert(`CSV contains ${data.length} vehicles — Vroom's limit is ${max}.\nReduce the file and try again.`);
            return;
          }
          if (total > max) {
            alert(`Adding ${data.length} vehicles would bring the total to ${total}, exceeding the ${max}-vehicle limit.\nClear some existing vehicles first.`);
            return;
          }
          if (data.length >= warn && !window.confirm(
            `⚠️ Large upload: ${data.length} vehicles (limit ${max}).\nVroom may be slow with many vehicles.\n\nContinue?`
          )) return;

          const parsed = [];
          data.forEach((row, i) => {
            try {
              const start = JSON.parse(row.start);
              const cap   = JSON.parse(row.capacity);
              if (!isValidCoord(start)) { rowErrors.push(`Row ${i + 2}: invalid start coordinate`); return; }
              if (!Array.isArray(cap) || cap.some(c => isNaN(c) || c < 0)) { rowErrors.push(`Row ${i + 2}: capacity must be a non-negative number array`); return; }
              parsed.push({
                id: getNextId(),
                start,
                end: row.end ? JSON.parse(row.end) : start,
                capacity: cap,
                skills: row.skills ? JSON.parse(row.skills) : [],
                time_window: row.time_window ? JSON.parse(row.time_window) : undefined,
              });
            } catch (err) { rowErrors.push(`Row ${i + 2}: parse error — ${err.message}`); }
          });

          if (rowErrors.length) {
            const proceed = window.confirm(`${rowErrors.length} row(s) had errors and were skipped:\n\n${rowErrors.slice(0, 5).join('\n')}${rowErrors.length > 5 ? `\n…and ${rowErrors.length - 5} more` : ''}\n\nAdd the ${parsed.length} valid rows?`);
            if (!proceed) return;
          }
          if (parsed.length === 0) { alert('No valid vehicles found in the file.'); return; }

          setVehicles(prev => [...prev, ...parsed]);
          updateGlobalIdCounter([parsed]);
          alert(`✅ Added ${parsed.length} vehicle${parsed.length !== 1 ? 's' : ''}${rowErrors.length ? ` (${rowErrors.length} skipped)` : ''}.`);
          return;
        }

        // ════════════════════════════════════
        // 📦 SHIPMENTS
        // ════════════════════════════════════
        if (cols.includes('pickup_id') && cols.includes('delivery_id')) {
          const required = ['pickup_location', 'delivery_location', 'amount'];
          const missing = required.filter(c => !cols.includes(c));
          if (missing.length) { alert(`Missing required columns: ${missing.join(', ')}`); return; }

          const { warn, max } = CSV_LIMITS.SHIPMENTS;
          const total = shipments.length + data.length;

          if (data.length > max) {
            alert(`CSV contains ${data.length} shipments — limit is ${max} (each shipment uses 2 Vroom locations).\nReduce the file and try again.`);
            return;
          }
          if (total > max) {
            alert(`Adding ${data.length} shipments would bring the total to ${total}, exceeding the ${max} limit.\nClear some existing shipments first.`);
            return;
          }
          if (data.length >= warn && !window.confirm(
            `⚠️ Large upload: ${data.length} shipments (limit ${max}).\n\nContinue?`
          )) return;

          const parsed = [];
          data.forEach((row, i) => {
            try {
              const pickupLoc   = JSON.parse(row.pickup_location);
              const deliveryLoc = JSON.parse(row.delivery_location);
              const amount      = JSON.parse(row.amount);
              if (!isValidCoord(pickupLoc))   { rowErrors.push(`Row ${i + 2}: invalid pickup_location`); return; }
              if (!isValidCoord(deliveryLoc)) { rowErrors.push(`Row ${i + 2}: invalid delivery_location`); return; }
              if (!Array.isArray(amount) || amount.some(a => isNaN(a) || a < 0)) { rowErrors.push(`Row ${i + 2}: amount must be a non-negative number array`); return; }
              parsed.push({
                amount,
                skills: row.skills ? JSON.parse(row.skills) : [],
                pickup:   { id: getNextId(), service: parseInt(row.pickup_service)   || 0, location: pickupLoc },
                delivery: { id: getNextId(), service: parseInt(row.delivery_service) || 0, location: deliveryLoc },
              });
            } catch (err) { rowErrors.push(`Row ${i + 2}: parse error — ${err.message}`); }
          });

          if (rowErrors.length) {
            const proceed = window.confirm(`${rowErrors.length} row(s) had errors and were skipped:\n\n${rowErrors.slice(0, 5).join('\n')}${rowErrors.length > 5 ? `\n…and ${rowErrors.length - 5} more` : ''}\n\nAdd the ${parsed.length} valid rows?`);
            if (!proceed) return;
          }
          if (parsed.length === 0) { alert('No valid shipments found in the file.'); return; }

          const incompatible = parsed.filter(s => vehicles.length > 0 && !vehicles.some(v => s.skills.every(sk => v.skills.includes(sk))));
          if (incompatible.length > 0 && !window.confirm(`⚠️ ${incompatible.length} shipment(s) have no matching vehicle skills.\n\nAdd them anyway?`)) {
            setShipments(prev => [...prev, ...parsed.filter(s => !incompatible.includes(s))]);
          } else {
            setShipments(prev => [...prev, ...parsed]);
          }

          updateGlobalIdCounter([parsed]);
          alert(`✅ Added ${parsed.length} shipment${parsed.length !== 1 ? 's' : ''}${rowErrors.length ? ` (${rowErrors.length} skipped)` : ''}.`);
          return;
        }

        // ════════════════════════════════════
        // 📍 JOBS
        // ════════════════════════════════════
        if (cols.includes('location') && cols.includes('service')) {
          const required = ['location', 'service'];
          const missing = required.filter(c => !cols.includes(c));
          if (missing.length) { alert(`Missing required columns: ${missing.join(', ')}`); return; }

          const { warn, max } = CSV_LIMITS.JOBS;
          const total = jobs.length + data.length;

          if (data.length > max) {
            alert(`CSV contains ${data.length} jobs — Vroom's location limit is ${max}.\nReduce the file and try again.`);
            return;
          }
          if (total > max) {
            alert(`Adding ${data.length} jobs would bring the total to ${total}, exceeding the ${max} limit.\nClear some existing jobs first.`);
            return;
          }
          if (data.length >= warn && !window.confirm(
            `⚠️ Large upload: ${data.length} jobs (limit ${max}).\nThis may take longer to optimise.\n\nContinue?`
          )) return;

          const parsed = [];
          data.forEach((row, i) => {
            try {
              const location = JSON.parse(row.location);
              const service  = parseInt(row.service);
              if (!isValidCoord(location)) { rowErrors.push(`Row ${i + 2}: invalid location coordinate`); return; }
              if (isNaN(service) || service < 0) { rowErrors.push(`Row ${i + 2}: service time must be a non-negative integer`); return; }
              parsed.push({
                id: getNextId(),
                service,
                location,
                delivery:     row.delivery     ? JSON.parse(row.delivery)     : undefined,
                pickup:       row.pickup        ? JSON.parse(row.pickup)       : undefined,
                skills:       row.skills        ? JSON.parse(row.skills)       : [],
                time_windows: row.time_windows  ? JSON.parse(row.time_windows) : undefined,
              });
            } catch (err) { rowErrors.push(`Row ${i + 2}: parse error — ${err.message}`); }
          });

          if (rowErrors.length) {
            const proceed = window.confirm(`${rowErrors.length} row(s) had errors and were skipped:\n\n${rowErrors.slice(0, 5).join('\n')}${rowErrors.length > 5 ? `\n…and ${rowErrors.length - 5} more` : ''}\n\nAdd the ${parsed.length} valid rows?`);
            if (!proceed) return;
          }
          if (parsed.length === 0) { alert('No valid jobs found in the file.'); return; }

          const incompatible = parsed.filter(j => vehicles.length > 0 && !vehicles.some(v => j.skills.every(sk => v.skills.includes(sk))));
          if (incompatible.length > 0 && !window.confirm(`⚠️ ${incompatible.length} job(s) have no matching vehicle skills.\n\nAdd them anyway?`)) {
            setJobs(prev => [...prev, ...parsed.filter(j => !incompatible.includes(j))]);
          } else {
            setJobs(prev => [...prev, ...parsed]);
          }

          updateGlobalIdCounter([parsed]);
          alert(`✅ Added ${parsed.length} job${parsed.length !== 1 ? 's' : ''}${rowErrors.length ? ` (${rowErrors.length} skipped)` : ''}.`);
          return;
        }

        alert('Unknown CSV structure.\n\nExpected columns:\n• Vehicles: start, capacity\n• Jobs: location, service\n• Shipments: pickup_id, delivery_id, pickup_location, delivery_location, amount');
      },
    });
    e.target.value = ''; // allow re-uploading the same file
  };


//  global id counter update after CSV import



const getNextId = () => {
  return globalIdCounter++;
};

const updateGlobalIdCounter = (datasets) => {
  // datasets = array of arrays: [vehicles, jobs, shipments]
  let maxId = 0;

  datasets.forEach((set) => {
    set.forEach((item) => {
      if (item.id && item.id > maxId) maxId = item.id;

      // shipments have nested pickup/delivery IDs
      if (item.pickup?.id && item.pickup.id > maxId) maxId = item.pickup.id;
      if (item.delivery?.id && item.delivery.id > maxId) maxId = item.delivery.id;
    });
  });

  globalIdCounter = maxId + 1;
};



  // ---------- API send ----------
  const handleSendToApi = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const finalJson = { vehicles, jobs, shipments };
      // console.log("Sending to Vroom:", JSON.stringify(finalJson, null, 2));
      const response = await fetch("https://geogridrouter.de/api/vroom/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(finalJson),
      });
      if (!response.ok) {
        const errText = await response.text();
        console.error("Vroom error response:", errText);
        throw new Error(`Error ${response.status}: ${errText.slice(0, 200)}`);
      }
      const data = await response.json();
      setApiResponse(data);
      setAnimStep(null);
      setAnimRouteIdx(0);
    } catch (err) {
      console.error("Vroom fetch error:", err);
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Export ZIP (unchanged logic) ----------
  const exportCsvAsZip = (apiResp) => {
    if (!apiResp) {
      alert("No API response to export");
      return;
    }

    const zip = new JSZip();

    // Summary
    const summary = apiResp.summary || {};
    let flatSummary = { ...summary };
    if (summary.computing_times) {
      Object.entries(summary.computing_times).forEach(([key, value]) => {
        flatSummary[`computing_time_${key}`] = value;
      });
      delete flatSummary.computing_times;
    }
    zip.file("summary.csv", Papa.unparse([flatSummary]));

    // Unassigned
    if (apiResp.unassigned) zip.file("unassigned.csv", Papa.unparse(apiResp.unassigned));

    // Routes
    const routes = (apiResp.routes || []).map((r) => {
      const { steps, ...rest } = r;
      // convert encoded polyline to WKT-like LINESTRING
      try {
        const geometrydecoded = polyline.decode(r.geometry || "");
        const decodedPoints = geometrydecoded.map((p) => `${p[0]} ${p[1]}`).join(", ");
        rest.geometry = `LINESTRING(${decodedPoints})`;
      } catch (err) {
        // leave geometry as-is if decode fails
      }
      return rest;
    });
    zip.file("routes.csv", Papa.unparse(routes));

    // Steps
    let steps = [];
    (apiResp.routes || []).forEach((route, routeIndex) => {
      (route.steps || []).forEach((s, stepIndex) => {
        steps.push({
          route: routeIndex + 1,
          step: stepIndex + 1,
          ...s,
        });
      });
    });
    zip.file("steps.csv", Papa.unparse(steps));

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "vroom_output.zip");
    });
  };

  // ---------- Route arrows component ----------
  const RouteArrows = ({ positions }) => {
    const map = useMap();
    useEffect(() => {
      if (!positions || positions.length === 0) return;

      const decorator = L.polylineDecorator(L.polyline(positions), {
        patterns: [
          {
            offset: "3%",
            repeat: "8%",
            symbol: L.Symbol.arrowHead({
              pixelSize: 7,
              polygon: false,
              pathOptions: { stroke: true, color: "black" },
            }),
          },
        ],
      });

      decorator.addTo(map);
      return () => {
        map.removeLayer(decorator);
      };
    }, [positions, map]);

    return null;
  };

  // ---------- UI ----------
  const finalJson = { vehicles, jobs, shipments };

  return (
    <div className="vroom-page">

      {/* Toolbar */}
      <div className="map-toolbar">

        {/* Navigate */}
        <div className="toolbar-group">
          <span className="toolbar-label">Navigate</span>
          <div className="search-wrap" style={{ position: 'relative' }}>
            <input
              type="text"
              className="toolbar-input search-input"
              placeholder="Search location..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') searchLocation(); }}
              style={{ width: '180px' }}
            />
            <button className="btn btn-secondary btn-sm" onClick={searchLocation}>Search</button>
            {showSearchDropdown && searchResults.length > 0 && (
              <div className="search-dropdown">
                {searchResults.map((r, i) => (
                  <button key={i} className="search-dropdown-item" onClick={() => selectSearchResult(r)}>
                    {r.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <select
            className="toolbar-select"
            value=""
            onChange={(e) => {
              const city = CITIES.find(c => c.name === e.target.value);
              if (city) setFlyTarget({ lat: city.lat, lng: city.lng });
            }}
          >
            <option value="">Jump to city...</option>
            {CITIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        <div className="toolbar-group">
          <span className="toolbar-label">Add</span>
          <button
            className={`btn btn-sm ${addMode === 'vehicle' ? 'btn-active' : 'btn-outline'}`}
            onClick={() => setAddMode(addMode === 'vehicle' ? null : 'vehicle')}
          >
            + Vehicle
          </button>
          <button
            className={`btn btn-sm ${addMode === 'job' ? 'btn-active' : 'btn-outline'}`}
            onClick={() => setAddMode(addMode === 'job' ? null : 'job')}
          >
            + Job
          </button>
          <button
            className={`btn btn-sm ${addMode === 'shipment' ? 'btn-active' : 'btn-outline'}`}
            onClick={() => {
              if (addMode === 'shipment') {
                setAddMode(null);
              } else {
                setAddMode('shipment');
                setShipmentPoints([]);
                setFormLocation(null);
              }
            }}
          >
            + Shipment
          </button>
          {addMode && (
            <span className="mode-hint">
              {addMode === 'shipment'
                ? shipmentPoints.length === 0
                  ? '① Click map for pickup point'
                  : '② Click map for delivery point'
                : 'Click map to place'}
            </span>
          )}
        </div>

        <div className="toolbar-group">
          <label className="btn btn-ghost btn-sm upload-label">
            Upload CSV
            <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} />
          </label>
        </div>

        <div className="toolbar-group">
          <button
            className={`btn btn-primary btn-sm`}
            onClick={handleSendToApi}
            disabled={loading}
          >
            {loading ? <><span className="spinner" />Getting routes...</> : 'Get the routes!'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsvAsZip(apiResponse)}>
            Download ZIP
          </button>
        </div>

        <div className="toolbar-group toolbar-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => setVehicles([])}>Clear Vehicles</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setJobs([])}>Clear Jobs</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShipments([])}>Clear Shipments</button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => {
              setVehicles([]);
              setJobs([]);
              setShipments([]);
              setApiResponse(null);
              globalIdCounter = 1;
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      {vehicles.length > 0 && (
        <div className="skills-banner">
          <strong>Skills available:</strong>{' '}
          {Array.from(new Set(vehicles.flatMap((v) => v.skills))).join(', ') || 'None'}
        </div>
      )}

      {/* Map */}
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={13}
        style={{ height: '65vh', width: '100%' }}
        className="vroom-map"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <MapFlyTo target={flyTarget} />
        <MapClickHandler />

        {vehicles.map((v) => (
          <Marker key={`v-${v.id}`} position={[v.start[1], v.start[0]]}>
            <Popup>
              <strong>Vehicle {v.id}</strong><br />
              Capacity: {v.capacity?.join?.(", ")}<br />
              Skills: {v.skills?.join?.(", ")}
            </Popup>
          </Marker>
        ))}

        {jobs.map((j) => (
          <Marker
            key={`j-${j.id}`}
            position={[j.location[1], j.location[0]]}
            icon={L.divIcon({ className: 'custom-job-marker', html: '📦' })}
          >
            <Popup>
              <strong>Job {j.id}</strong><br />
              Service: {j.service}s<br />
              Skills: {j.skills?.join?.(", ")}
            </Popup>
          </Marker>
        ))}

        {shipments.map((shipment) => (
          <React.Fragment key={`shipment-${shipment.pickup.id}`}>
            <Marker
              position={[shipment.pickup.location[1], shipment.pickup.location[0]]}
              icon={L.divIcon({ className: 'shipment-marker', html: '📦 P', iconSize: [20, 20] })}
            >
              <Popup>
                <strong>Pickup</strong> — ID: {shipment.pickup.id}<br />
                Service: {shipment.pickup.service}s
              </Popup>
            </Marker>
            <Marker
              position={[shipment.delivery.location[1], shipment.delivery.location[0]]}
              icon={L.divIcon({ className: 'shipment-marker', html: '📦 D', iconSize: [20, 20] })}
            >
              <Popup>
                <strong>Delivery</strong> — ID: {shipment.delivery.id}<br />
                Service: {shipment.delivery.service}s
              </Popup>
            </Marker>
          </React.Fragment>
        ))}

        {apiResponse?.routes?.map((route, index) => {
          if (!route.geometry) return null;
          if (animStep !== null && index !== animRouteIdx) return null;

          const color = ['#3b82f6', '#ef4444', '#22c55e', '#a855f7'][index % 4];

          if (animStep !== null && route.steps) {
            const visibleSteps = route.steps.slice(0, animStep + 1).filter(s => s.location);
            const stepPositions = visibleSteps.map(s => [s.location[1], s.location[0]]);
            return (
              <React.Fragment key={index}>
                {stepPositions.length > 1 && (
                  <Polyline positions={stepPositions} pathOptions={{ color, weight: 4 }} />
                )}
                {visibleSteps.map((step, si) => (
                  <Marker
                    key={si}
                    position={[step.location[1], step.location[0]]}
                    icon={L.divIcon({
                      className: '',
                      html: `<div style="background:${si === visibleSteps.length - 1 ? color : '#94a3b8'};color:#fff;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)">${si + 1}</div>`,
                      iconSize: [22, 22],
                      iconAnchor: [11, 11],
                    })}
                  >
                    <Popup>
                      <strong>Step {si + 1}</strong><br />
                      Type: {step.type}{step.id != null ? <><br />ID: {step.id}</> : null}
                    </Popup>
                  </Marker>
                ))}
              </React.Fragment>
            );
          }

          const decoded = polyline.decode(route.geometry).map(([lat, lng]) => [lat, lng]);
          return (
            <React.Fragment key={index}>
              <Polyline positions={decoded} pathOptions={{ color, weight: 4 }} />
              <RouteArrows positions={decoded} />
            </React.Fragment>
          );
        })}
      </MapContainer>

      {loading && <div className="status-banner">Sending request to API...</div>}
      {apiError && <div className="error-banner">&#9888; {apiError}</div>}

      {/* Add form modal */}
      {formLocation && addMode && (
        <div className="modal-overlay">
          <div className="modal-card">

            {addMode === 'vehicle' && (
              <form onSubmit={handleSubmitVehicle}>
                <h2 className="modal-title">Add Vehicle</h2>
                <p className="modal-desc">Location pinned on map. Pick a preset or fill manually.</p>

                {/* Preset grid */}
                <div className="preset-grid">
                  {VEHICLE_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      className="preset-card"
                      title={p.desc}
                      onClick={() => applyVehiclePreset(p)}
                    >
                      <span className="preset-icon">{p.icon}</span>
                      <span className="preset-label">{p.label}</span>
                      <span className="preset-desc">{p.desc}</span>
                    </button>
                  ))}
                </div>

                <div className="form-field">
                  <label className="form-label">Capacity</label>
                  <input name="capacity" type="number" className="modal-input" placeholder="e.g. 100"
                    value={vCapacity} onChange={e => setVCapacity(e.target.value)} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Skills (comma-separated integers)</label>
                  <input name="skills" className="modal-input" placeholder="e.g. 1,2  or leave empty"
                    value={vSkills} onChange={e => setVSkills(e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-label">Start Time</label>
                  <input type="datetime-local" name="start_time" className="modal-input"
                    value={vStartTime} onChange={e => setVStartTime(e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-label">End Time</label>
                  <input type="datetime-local" name="end_time" className="modal-input"
                    value={vEndTime} onChange={e => setVEndTime(e.target.value)} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => { setFormLocation(null); setAddMode(null); resetVehicleForm(); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Add Vehicle</button>
                </div>
              </form>
            )}

            {addMode === 'job' && (
              <form onSubmit={handleSubmitJob}>
                <h2 className="modal-title">Add Job</h2>
                <p className="modal-desc">Location pinned on map.</p>
                <div className="form-field">
                  <label className="form-label">Type</label>
                  <select name="jobType" className="modal-select" required>
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Quantity</label>
                  <input name="quantity" type="number" className="modal-input" placeholder="e.g. 10" required />
                </div>
                <div className="form-field">
                  <label className="form-label">Service Time (sec)</label>
                  <input name="service" type="number" className="modal-input" placeholder="e.g. 300" required />
                </div>
                <div className="form-field">
                  <label className="form-label">Skills (comma-separated)</label>
                  <input name="skills" className="modal-input" placeholder="e.g. 1,2" required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => { setFormLocation(null); setAddMode(null); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Add Job</button>
                </div>
              </form>
            )}

            {addMode === 'shipment' && Array.isArray(formLocation) && formLocation.length === 2 && (
              <form onSubmit={handleSubmitShipment}>
                <h2 className="modal-title">Add Shipment</h2>
                <p className="modal-desc">Pickup and delivery points set.</p>
                <div className="form-field">
                  <label className="form-label">Amount</label>
                  <input name="amount" type="number" className="modal-input" placeholder="e.g. 5" required />
                </div>
                <div className="form-field">
                  <label className="form-label">Skills (comma-separated)</label>
                  <input name="skills" className="modal-input" placeholder="e.g. 1,2" required />
                </div>
                <div className="form-field">
                  <label className="form-label">Pickup Service Time (sec)</label>
                  <input name="servicePickup" type="number" className="modal-input" placeholder="e.g. 300" required />
                </div>
                <div className="form-field">
                  <label className="form-label">Delivery Service Time (sec)</label>
                  <input name="serviceDelivery" type="number" className="modal-input" placeholder="e.g. 300" required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => { setFormLocation(null); setAddMode(null); setShipmentPoints([]); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Add Shipment</button>
                </div>
              </form>
            )}

          </div>
        </div>
      )}

      {/* Bottom panels */}
      <div className="panels-row">
        <div className="panel">
          <div className="panel-header">
            <h3 className="panel-title">JSON Preview</h3>
            <span className="badge">
              {vehicles.length}V · {jobs.length}J · {shipments.length}S
            </span>
          </div>
          <pre className="feature-json">{JSON.stringify(finalJson, null, 2)}</pre>
        </div>

        <div className="panel">
          <div className="panel-header">
            <h3 className="panel-title">Entities</h3>
          </div>

          <div className="entity-section">
            <h4 className="entity-heading">
              Vehicles <span className="badge">{vehicles.length}</span>
            </h4>
            {vehicles.length === 0 ? (
              <p className="empty-state">No vehicles added.</p>
            ) : (
              <ul className="entity-list">
                {vehicles.map((v) => (
                  <li key={v.id} className="entity-item">
                    <span>ID {v.id} · cap: {v.capacity?.join?.(", ")} · skills: {v.skills?.join(", ") || 'none'}</span>
                    <div className="entity-actions">
                      <button className="btn btn-sm entity-edit" onClick={() => setEditingEntity({ type: 'vehicle', data: v })}>✏</button>
                      <button className="btn btn-sm entity-delete" onClick={() => handleDeleteVehicle(v.id)}>&#x2715;</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="entity-section">
            <h4 className="entity-heading">
              Jobs <span className="badge">{jobs.length}</span>
            </h4>
            {jobs.length === 0 ? (
              <p className="empty-state">No jobs added.</p>
            ) : (
              <ul className="entity-list">
                {jobs.map((j) => (
                  <li key={j.id} className="entity-item">
                    <span>ID {j.id} · service: {j.service}s · skills: {j.skills?.join(", ") || 'none'}</span>
                    <div className="entity-actions">
                      <button className="btn btn-sm entity-edit" onClick={() => setEditingEntity({ type: 'job', data: j })}>✏</button>
                      <button className="btn btn-sm entity-delete" onClick={() => handleDeleteJob(j.id)}>&#x2715;</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="entity-section">
            <h4 className="entity-heading">
              Shipments <span className="badge">{shipments.length}</span>
            </h4>
            {shipments.length === 0 ? (
              <p className="empty-state">No shipments added.</p>
            ) : (
              <ul className="entity-list">
                {shipments.map((s, i) => (
                  <li key={i} className="entity-item">
                    <span>Amount: {s.amount?.join?.(", ")} · skills: {s.skills?.join(", ") || 'none'}</span>
                    <div className="entity-actions">
                      <button className="btn btn-sm entity-edit" onClick={() => setEditingEntity({ type: 'shipment', data: s })}>✏</button>
                      <button className="btn btn-sm entity-delete" onClick={() => handleDeleteShipment(s.id)}>&#x2715;</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {apiResponse && (
        <div className="panel">
          <div className="panel-header">
            <h3 className="panel-title">API Response</h3>
            <span className="badge badge-success">{apiResponse.routes?.length || 0} routes</span>
          </div>
          <pre className="feature-json feature-json-success">{JSON.stringify(apiResponse, null, 2)}</pre>
        </div>
      )}

      {apiResponse?.routes?.length > 0 && (
        <div className="anim-controls">
          <div className="anim-header">
            <strong>Route Animation</strong>
            {apiResponse.routes.length > 1 && (
              <select
                className="anim-route-select"
                value={animRouteIdx}
                onChange={(e) => { setAnimRouteIdx(Number(e.target.value)); setAnimStep(null); }}
              >
                {apiResponse.routes.map((_, i) => (
                  <option key={i} value={i}>Vehicle {i + 1}</option>
                ))}
              </select>
            )}
          </div>
          <div className="anim-btn-row">
            <button className="btn btn-sm btn-outline" onClick={() => setAnimStep(null)} disabled={animStep === null}>
              Show All
            </button>
            <button
              className="btn btn-sm btn-outline"
              disabled={animStep === null || animStep === 0}
              onClick={() => setAnimStep(s => Math.max(0, s - 1))}
            >
              ← Prev
            </button>
            <span className="anim-counter">
              {animStep === null
                ? `${apiResponse.routes[animRouteIdx]?.steps?.length ?? 0} steps`
                : `Step ${animStep + 1} / ${apiResponse.routes[animRouteIdx]?.steps?.length ?? '?'}`}
            </span>
            <button
              className="btn btn-sm btn-outline"
              disabled={animStep !== null && animStep >= (apiResponse.routes[animRouteIdx]?.steps?.length ?? 0) - 1}
              onClick={() => setAnimStep(s => s === null ? 0 : s + 1)}
            >
              {animStep === null ? 'Start →' : 'Next →'}
            </button>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingEntity && (
        <div className="modal-overlay">
          <div className="modal-card">

            {editingEntity.type === 'vehicle' && (
              <form onSubmit={(e) => handleUpdateVehicle(e, editingEntity.data.id)}>
                <h2 className="modal-title">Edit Vehicle {editingEntity.data.id}</h2>
                <p className="modal-desc">Location is unchanged.</p>
                <div className="form-field">
                  <label className="form-label">Capacity</label>
                  <input name="capacity" type="number" className="modal-input" defaultValue={editingEntity.data.capacity?.[0]} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Skills (comma-separated)</label>
                  <input name="skills" className="modal-input" defaultValue={editingEntity.data.skills?.join(',')} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Start Time</label>
                  <input type="datetime-local" name="start_time" className="modal-input" />
                </div>
                <div className="form-field">
                  <label className="form-label">End Time</label>
                  <input type="datetime-local" name="end_time" className="modal-input" />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingEntity(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </form>
            )}

            {editingEntity.type === 'job' && (
              <form onSubmit={(e) => handleUpdateJob(e, editingEntity.data.id)}>
                <h2 className="modal-title">Edit Job {editingEntity.data.id}</h2>
                <p className="modal-desc">Location is unchanged.</p>
                <div className="form-field">
                  <label className="form-label">Type</label>
                  <select name="jobType" className="modal-select" defaultValue={editingEntity.data.pickup ? 'pickup' : 'delivery'} required>
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Quantity</label>
                  <input name="quantity" type="number" className="modal-input" defaultValue={editingEntity.data.pickup?.[0] ?? editingEntity.data.delivery?.[0]} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Service Time (sec)</label>
                  <input name="service" type="number" className="modal-input" defaultValue={editingEntity.data.service} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Skills (comma-separated)</label>
                  <input name="skills" className="modal-input" defaultValue={editingEntity.data.skills?.join(',')} required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingEntity(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </form>
            )}

            {editingEntity.type === 'shipment' && (
              <form onSubmit={(e) => handleUpdateShipment(e, editingEntity.data.pickup?.id)}>
                <h2 className="modal-title">Edit Shipment</h2>
                <p className="modal-desc">Locations are unchanged.</p>
                <div className="form-field">
                  <label className="form-label">Amount</label>
                  <input name="amount" type="number" className="modal-input" defaultValue={editingEntity.data.amount?.[0]} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Skills (comma-separated)</label>
                  <input name="skills" className="modal-input" defaultValue={editingEntity.data.skills?.join(',')} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Pickup Service Time (sec)</label>
                  <input name="servicePickup" type="number" className="modal-input" defaultValue={editingEntity.data.pickup?.service} required />
                </div>
                <div className="form-field">
                  <label className="form-label">Delivery Service Time (sec)</label>
                  <input name="serviceDelivery" type="number" className="modal-input" defaultValue={editingEntity.data.delivery?.service} required />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingEntity(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </form>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
